import type { AttributeDataType } from "./attributeCatalogue";
import type { ConjunctionOperator, FilterOperator } from "./types";

type SelectOption<T> = { value: T; label: string };

/** All filter operators, with the labels rendered inside the dropdown.
 * Stable order — UI consumers should filter (via `operatorsForDataType`)
 * rather than re-sort. */
export const OPERATOR_OPTIONS: ReadonlyArray<SelectOption<FilterOperator>> = [
  { value: "eq", label: "=" },
  { value: "neq", label: "\u2260" },
  { value: "contains", label: "contains" },
  { value: "gt", label: ">" },
  { value: "gte", label: "\u2265" },
  { value: "lt", label: "<" },
  { value: "lte", label: "\u2264" },
  { value: "in", label: "is any of" },
  { value: "not_in", label: "is none of" },
];

// Per-data-type allow lists. Defaults chosen to mirror the canonical SQL
// semantics until design signs off on a different cut:
//  - Inequalities (`gt`/`gte`/`lt`/`lte`) for ordered types (number / date / time).
//  - `contains` only for free-text scalars.
//  - Booleans only support equality and set membership.
//  - `in` / `not_in` everywhere (sets work over any comparable type).
const ORDERED: ReadonlyArray<FilterOperator> = [
  "eq",
  "neq",
  "gt",
  "gte",
  "lt",
  "lte",
  "in",
  "not_in",
];
const EQUALITY_AND_SET: ReadonlyArray<FilterOperator> = ["eq", "neq", "in", "not_in"];

const OPERATORS_BY_DATA_TYPE: Record<AttributeDataType, ReadonlyArray<FilterOperator>> = {
  NUMBER: ORDERED,
  CURRENCY: ORDERED,
  PERCENTAGE: ORDERED,
  DATE: ORDERED,
  TIME: ORDERED,
  BOOLEAN: EQUALITY_AND_SET,
  TEXT: ["eq", "neq", "contains", "in", "not_in"],
};

/** Subset of `OPERATOR_OPTIONS` valid for the given data type. Falls back to
 * the full list when no attribute is selected yet, so the dropdown is never
 * empty. */
export const operatorsForDataType = (
  dataType: AttributeDataType | undefined,
): ReadonlyArray<SelectOption<FilterOperator>> => {
  if (!dataType) return OPERATOR_OPTIONS;
  const allowed = new Set(OPERATORS_BY_DATA_TYPE[dataType]);
  return OPERATOR_OPTIONS.filter((option) => allowed.has(option.value));
};

/** Defaults for brand-new rule sets / groups: groups joined by AND, clauses
 * joined by OR. Existing data round-trips verbatim. */
export const DEFAULT_GROUPS_OPERATOR: ConjunctionOperator = "and";
export const DEFAULT_CONDITION_OPERATOR: ConjunctionOperator = "or";
